'use client';

import type { ReactNode } from 'react';
import { tw } from '@tw';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { QuizProgressRow } from '@/components/quiz/QuizProgressRow';
import { QuizFinishedCard } from '@/components/quiz/QuizFinishedCard';
import { QuizResult } from '@/components/quiz/QuizResult';
import { QuizOption } from '@/components/quiz/QuizOption';
import { QuizSlam, slamDuration } from '@/components/quiz/QuizSlam';
import { useQuizPlay } from '@/components/quiz/useQuizPlay';
import { QUIZ_CARD_MIN_H } from '@/components/quiz/quiz-card';
import type { SanitizedQuiz } from '@/lib/quiz/sanitize';

interface QuizCardProps {
  quiz: SanitizedQuiz;
  /** The owner previewing their own page. Analytics only — an owner playing
      their own quiz is testing it, and must not count as a visitor engaging. */
  isOwner: boolean;
  tutorUsername: string;
  /** The name the result's headline says. */
  firstName: string;
  loggedIn: boolean;
  /** On the mate page: the referral edge the gate's sign-in carries as `sourceSquad`. */
  squadAlias?: string | null;
  /** The DM reply the funnel link carried (`?r=`): the DM's follow-up reads the play by it. */
  dmReplyId?: string | null;
  /** Under a result the server sent no answers for: the way to them (a page on subscriptions). */
  unlockAnswers?: ReactNode;
}

/**
 * Quiz text, sealed. Not translated (on a language quiz an auto-translation
 * answers the question rather than distorting it), not selectable, and no iOS
 * callout offering Copy and Translate. One place decides what that means.
 */
function Sealed({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span translate="no" className={tw('sf-sealed notranslate', className)}>
      {children}
    </span>
  );
}

export function QuizCard({
  quiz,
  isOwner,
  tutorUsername,
  firstName,
  loggedIn,
  squadAlias,
  dmReplyId = null,
  unlockAnswers,
}: QuizCardProps) {
  const {
    phase,
    questions,
    currentIdx,
    total,
    flash,
    error,
    result,
    missed,
    answered,
    handleAnswer,
    onGateSuccess,
    cardRef,
    lockedHeight,
    slam,
    clearSlam,
  } = useQuizPlay({ quiz, isOwner, tutorUsername, loggedIn, dmReplyId });

  if (phase === 'result' && result) {
    // The score, then the questions worth a call, with the answers only for a member
    // (the server sends them to nobody else). The card sells nothing: its calendar
    // came out 2026-09-24, 7 buyers a quarter beside the intro card's 17.
    return (
      <QuizFinishedCard>
        <QuizResult
          score={result.score}
          total={result.totalQuestions}
          name={firstName}
          missed={missed}
          hardest={result.hardest}
          unlock={missed.length > 0 && !answered ? unlockAnswers : undefined}
        />
      </QuizFinishedCard>
    );
  }

  if (phase === 'gate') {
    return (
      <div
        style={lockedHeight ? { minHeight: lockedHeight } : undefined}
        className={`sf-quiz-card flex ${QUIZ_CARD_MIN_H} flex-col gap-3 rounded-[var(--sf-radius)] p-5 text-white`}
      >
        {/* No quiz title: this screen is one sentence and a door. */}
        <span className="sf-title text-title-2 font-extrabold">
          Your score is ready — sign in to see it
        </span>
        <div className="mt-auto">
          <InlineAuth
            id={`quiz-${quiz.id}`}
            source="quiz_answers"
            tutorUsername={tutorUsername}
            sourceSquad={squadAlias}
            tone="accent"
            cta="Show my score"
            onSuccess={onGateSuccess}
          />
        </div>
      </div>
    );
  }

  if (phase === 'finishing') {
    return (
      <div
        style={{
          ...(lockedHeight ? { minHeight: lockedHeight } : {}),
          ...slamDuration,
        }}
        className={`sf-quiz-card flex ${QUIZ_CARD_MIN_H} flex-col items-center justify-center rounded-[var(--sf-radius)] p-5 text-white`}
      >
        {/* The last slam of the run — a spinner and a 14px caption made the end
            of a game look like a form submitting. */}
        <Sealed className="sf-slam-word sf-slam-stay">THAT&rsquo;S A WRAP</Sealed>
      </div>
    );
  }

  const question = questions[currentIdx];
  if (!question) return null;

  return (
    <div
      ref={cardRef}
      className={`sf-quiz-card relative flex ${QUIZ_CARD_MIN_H} flex-col gap-3 overflow-hidden rounded-[var(--sf-radius)] p-5 text-white`}
    >
      <QuizSlam slam={slam} onDone={clearSlam}>
        <Sealed className="sf-slam-word">{slam?.text}</Sealed>
      </QuizSlam>
      <QuizProgressRow at={Math.min(currentIdx + 1, total)} of={total} inert={!!slam} />
      <div className="grid flex-1">
        {questions.map((q, qi) => {
          const active = qi === currentIdx;
          return (
            <div
              key={q.id}
              aria-hidden={!active}
              className={tw(
                'col-start-1 row-start-1 flex flex-col gap-2.5',
                !active && 'invisible',
                active && currentIdx > 0 && 'sf-q-switch'
              )}
            >
              <Sealed className="sf-title text-title-2 font-extrabold">{q.text}</Sealed>
              <div className="mt-auto flex flex-col gap-1.5">
                {q.options.map((option, i) => {
                  const isFlashed = active && flash?.optionId === option.id;
                  return (
                    <QuizOption
                      key={option.id}
                      index={i}
                      disabled={!active || !!flash}
                      flashed={isFlashed}
                      onPick={() => void handleAnswer(option.id)}
                    >
                      <Sealed>{option.label}</Sealed>
                    </QuizOption>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {error && <span className="text-p-3 font-semibold text-white">{error}</span>}
    </div>
  );
}
